import * as routesNames from '../routes/routeName'

/**
 * SUB-MODULE keys (generated from reports/submodule-map.md — the audited map of
 * the portal's two nav surfaces: the top-nav dropdowns in workspaceTabs.js and
 * the left sidebar in sidebar.js).
 *
 * Permissions are held on SUB-MODULES; a top-nav tab is DERIVED — it renders
 * when the user holds at least one of its sub-modules (see the `parents` list from
 * useMyModules). These two maps are how the nav and the routes find the finest
 * key that applies.
 *
 * NOT gated here on purpose (gating them would REMOVE access people have
 * today, because nothing gates them now): /pharmacy-sms,
 * /pharmacy-pharmacist-signature, the mobile accountability upload-pics link,
 * and the two Appointments routes.
 */

/**
 * pathname -> the sub-module keys that unlock it. More than one key means ANY
 * of them is enough: the same page can sit under two menus (Investigation Rx
 * lives under both DISPENSE and ACCOUNTABILITY) or under a group header and
 * its first item (PACMed / Create New Order).
 */
const ROUTE_SUB_MODULE_PAIRS = [
    [routesNames.ADD_PATIENTS, ['patients_add_patient']],
    [routesNames.ALL_PATIENTS, ['patients_active', 'patients_inactive']],
    [routesNames.pharmacy_patient_route, ['patients_route']],
    [routesNames.PHARMACY_INFORMATION_UPDATE_FORM, ['forms_pharmacy_information_update']],
    [routesNames.PHARMACY_PRESCRIPTION_TRASNSFER_FORM, ['forms_rx_transfer_request']],
    [routesNames.pharmacy_original_prescription_form, ['forms_original_rx_copy_request']],
    [routesNames.pharmacy_Emergency_Supply_OAT_medications_form, ['forms_dr_notification_emergency_supply']],
    [routesNames.pharmacy_medication_form, ['forms_anti_viral_request']],
    [routesNames.missing_Rx_Requirements, ['forms_missing_rx_requirements']],
    [routesNames.pharmacy_emergency_supply_continuity_form, ['forms_emergency_supply_continuity']],
    [routesNames.patient_agreement_for_take_home_dosing, ['forms_take_home_dosing_agreement']],
    [routesNames.pharmacy_Plan_G_Form, ['forms_plan_g']],
    [routesNames.pharmacy_Plan_Z_Form, ['forms_plan_z']],
    [routesNames.pharmacy_doctor_communication_form, ['forms_doctor_communication']],
    [routesNames.pharmacy_Pharmacist_Prescriber_Communication, ['forms_pharmacist_prescriber_communication']],
    [routesNames.pharmacy_doctor_note_form, ['forms_doctor_note']],
    [routesNames.pharmacy_tb_form, ['forms_tb_screening']],
    [routesNames.pharmacy_tb_skin_test_mantoux_form, ['forms_tb_skin_test_mantoux']],
    [routesNames.pharmacy_tb_skin_test_logs, ['forms_tb_skin_test_logs']],
    [routesNames.pharmacy_tb_screening_form_logs, ['forms_tb_screening_logs']],
    [routesNames.pharmacy_order_compounding_form, ['compound_order_compound']],
    [routesNames.pharmacy_compounds, ['compound_price_list']],
    [routesNames.pharmacy_order_compound_form, ['compound_order_form']],
    [routesNames.pharmacy_controlled_order_request, ['compound_controlled_order_request']],
    [routesNames.pharmacy_compound_agreement_form, ['compound_agreement_form']],
    [routesNames.pharmacy_compound_quotations, ['compound_quotation']],
    [routesNames.pharmacy_compound_due_billing, ['compound_due_billing']],
    [routesNames.pharmacy_archived_deliveries, ['dispense_archived_deliveries']],
    [routesNames.pharmacy_consent_form_logs, ['dispense_consent_submissions']],
    [routesNames.pharmacy_patient_counselling, ['dispense_counselling']],
    [routesNames.pharmacy_expiring_rx_v2, ['dispense_expiring_rx_v2']],
    [routesNames.pharmacy_expiring_rx, ['dispense_expiring_rx']],
    [routesNames.pharmacy_investigate_rx, ['dispense_investigation_rx', 'accountability_investigation_rx']],
    [routesNames.pharmacy_rxnumber_date_sync, ['dispense_mark_delivered']],
    [routesNames.pharmacy_delivery_lables, ['dispense_labels']],
    [routesNames.pharmacy_patient_soap_notes, ['dispense_med_review']],
    [routesNames.pharmacy_med_review_logs, ['dispense_med_review_logs']],
    [routesNames.pharmacy_soap_notes_logs, ['dispense_soap_note_logs']],
    [routesNames.pharmacy_med_review_logs_v2, ['dispense_med_review_logs_v2']],
    [routesNames.pharmacy_tranfer_patient_concent, ['dispense_transfer_consents']],
    [routesNames.pharmacy_super_nova_verification, ['dispense_super_nova']],
    [routesNames.pharmacy_half_baked, ['dispense_half_baked']],
    [routesNames.pharmacy_drugs_billing, ['dispense_billing']],
    [routesNames.pharmacy_oat_prescriptions, ['dispense_prescriptions']],
    [routesNames.pharmacy_memo, ['dispense_memo']],
    [routesNames.pharmacy_ongoing_prescritiptions, ['dispense_ongoing_prescriptions']],
    [routesNames.pharmacy_returned_patches, ['dispense_returned_patches']],
    [routesNames.pharmacy_blister_patients, ['blisters_add_blister_pack']],
    [routesNames.pharmacy_blister_packs, ['blisters_blister_packs']],
    [routesNames.pharmacy_blister_daily_packs, ['blisters_daily_packs']],
    [routesNames.pharmacy_blister_pack_on_shelf, ['blisters_packs_on_shelf']],
    [routesNames.pharmacy_patient_vials_pack, ['vials_add_vials_pack']],
    [routesNames.pharmacy_vials_packs, ['vials_vials_packs']],
    [routesNames.pharmacy_dailyvials_packs, ['vials_daily_vials']],
    [routesNames.pharmacy_speciality_pack, ['speciality_medication_add_pack']],
    [routesNames.pharmacy_speciality_medication_packs, ['speciality_medication_packs']],
    [routesNames.pharmacy_dailymedication_packs, ['speciality_medication_daily']],
    [routesNames.pharmacy_generate_invoice, ['invoice_generate']],
    [routesNames.pharmacy_previous_invoices, ['invoice_previous']],
    [routesNames.ADD_ARV, ['arv_add']],
    [routesNames.ARV_LIST, ['arv_list']],
    [routesNames.pharmacy_daily_revenue, ['revenue_daily']],
    [routesNames.pharmacy_cash_change, ['revenue_cash_change']],
    [routesNames.pharmacy_deposit_info, ['revenue_deposit_info']],
    [routesNames.pharmacy_hand_cash, ['revenue_in_hand_cash']],
    [routesNames.create_new_fda_form, ['fda_form_create_new']],
    [routesNames.all_generated_fda_form_list, ['fda_form_all_generated']],
    [routesNames.pharmacy_expiring_frequent_dispense, ['fda_form_expiring']],
    [routesNames.pharmacy_expired_frequent_dispense, ['fda_form_expired']],
    [routesNames.pharmacy_frequent_dispensing_authorization_timeline, ['fda_form_doctor_timelines']],
    [routesNames.pharmacy_fda_audit, ['fda_form_audit']],
    [routesNames.pharmacy_inventory_list, ['inventory_list']],
    [routesNames.pharmacy_accountability_today_missed_rx, ['accountability_actions']],
    [routesNames.pharmacy_accountability_new_rx, ['accountability_new_rx']],
    [routesNames.pharmacy_accountability_prep_check, ['accountability_prep_check']],
    [routesNames.pharmacy_accountability_prep_for_new_rx, ['accountability_prep_for_new_rx']],
    [routesNames.pharmacy_accountability_after_process_prep_check, ['accountability_after_process_prep_check']],
    [routesNames.pharmacy_accountability_final_check, ['accountability_final_check']],
    [routesNames.pharmacy_accountability_all_reports, ['accountability_reports']],
    [routesNames.pharmacy_accounts, ['accountability_financials']],
    [routesNames.pharmacy_pharmacare, ['accountability_pharmacare']],
    [routesNames.pharmacy_add_travel_pack, ['travel_patients']],
    [routesNames.pharmacy_travel_logs, ['travel_logs']],
    [routesNames.pharmacy_billing_due, ['travel_billing_due']],
    [routesNames.pharmacy_travel_returning_patients, ['travel_returning_patients']],
    [routesNames.pharmacy_vaccine_passport, ['vaccine_passport']],
    [routesNames.pharmacy_vaccine_logs, ['vaccine_certificate_logs']],
    // APPOINTMENTS: both sub-modules are seeded in the registry and both routes
    // are live, but neither was mapped here — so these two pages were the only
    // sub-module-backed pages in the app with no gate at all.
    [routesNames.pharmacy_juno_appointment, ['appointments_book']],
    [routesNames.pharmacy_appointment_logs, ['appointments_logs']],
    [routesNames.pharmacy_blister_packaging_processing, ['packaging_blister', 'packaging_blister_create_order']],
    [routesNames.pharmacy_blister_packaging_prn_processing, ['packaging_blister_prn_processing']],
    [routesNames.pharmacy_blister_packaging_liquid_processing, ['packaging_blister_liquid_processing']],
    [routesNames.pharmacy_blister_packaging_orders, ['packaging_blister_incoming_orders']],
    [routesNames.pharmacy_blister_packaging_packmed_files, ['packaging_blister_my_files']],
    [routesNames.pharmacy_blister_packaging_invoices, ['packaging_blister_invoice']],
    [routesNames.pharmacy_blister_packaging_monthly_invoice, ['packaging_blister_monthly_invoice']],
    [routesNames.pharmacy_packaging_processing, ['packaging_pacmed', 'packaging_pacmed_create_order']],
    [routesNames.pharmacy_packaging_orders, ['packaging_pacmed_incoming_orders']],
    [routesNames.pharmacy_packaging_all_d_logs, ['packaging_pacmed_my_files']],
    [routesNames.pharmacy_packaging_din_management, ['packaging_pacmed', 'packaging_pacmed_create_order']],
    [routesNames.pharmacy_packaging_invoices, ['packaging_pacmed_invoice']],
    [routesNames.pharmacy_packaging_prn_processing, ['packaging_pacmed_prn_processing']],
    [routesNames.pharmacy_packaging_liquid_processing, ['packaging_pacmed_liquid_processing']],
    [routesNames.pharmacy_packaging_monthly_invoice, ['packaging_pacmed_monthly_invoice']],
    [routesNames.pharmacy_packaging_analytics, ['packaging_analytics']],
    [routesNames.pharmacy_app_prescriptions, ['app_prescriptions']],
    [routesNames.pharmacy_app_notification_list, ['app_custom_notification']],
    [routesNames.pharmacy_app_pharmacy_services, ['app_pharmacy_services']],
    [routesNames.pharmacy_app_pharmacy_services_alias, ['app_pharmacy_services']],
    [routesNames.pharmacy_app_patient_chat, ['app_patient_chat']],
    [routesNames.pharmacy_user, ['team_your_team']],
    [routesNames.pharmacy_timeoff, ['team_time_off_request']],
    [routesNames.pharmacy_team_attendance, ['team_timesheet']],
    [routesNames.pharmacy_certificate_template, ['team_certificate_template']],
    [routesNames.pharmacy_assign_certificate, ['team_assign_template']],
    [routesNames.pharmacy_check_out_in, ['team_checkin_checkout_logs']],
    [routesNames.pharmacy_department, ['team_departments']],
    [routesNames.pharmacy_team_avalibility, ['team_schedule']],
    [routesNames.PHARMACY_PATIENT_PROFILE, ['patients_active']],
    [routesNames.pharmacy_add_patient_route, ['patients_route']],
    [routesNames.pharmacy_compound_price_list, ['compound_price_list']],
    [routesNames.pharmacy_dwi, ['dispense']],
    [routesNames.pharmacy_rx_signature_request_list, ['dispense_investigation_rx']],
    [routesNames.pharmacy_create_soap_notes, ['dispense_med_review']],
    [routesNames.pharmacy_reversed_deliveries, ['dispense']],
    [routesNames.pharmacy_accountability, ['accountability_new_rx']],
    [routesNames.pharmacy_mark_flexi, ['accountability_prep_check']],
    [routesNames.pharmacy_erx, ['accountability']],
    [routesNames.pharmacy_patient_blister_pack, ['blisters_add_blister_pack']],
    [routesNames.pharmacy_blister_sheets, ['blisters_blister_packs']],
    [routesNames.pharmacy_blister_sheets_report, ['blisters_blister_packs']],
    [routesNames.pharmacy_add_vials_patient, ['vials_add_vials_pack']],
    [routesNames.pharmacy_add_medication_patients, ['speciality_medication_add_pack']],
    [routesNames.pharmacy_add_travel_patient, ['travel_patients']],
    [routesNames.pharmacy_packaging_order_preview, ['packaging']],
    [routesNames.pharmacy_order_transfer, ['app_prescriptions']],
    [routesNames.pharmacy_order_new, ['app_prescriptions']],
    [routesNames.pharmacy_view_order_details, ['app_prescriptions']],
    [routesNames.pharmacy_app_refill_request, ['app_prescriptions']],
    [routesNames.pharmacy_app_user_list, ['app']],
    [routesNames.pharmacy_app_announcements, ['app']],
    [routesNames.pharmacy_app_insurance_documents, ['app']],
    [routesNames.pharmacy_assign_certificate_employee, ['team_assign_template']],
    [routesNames.pharmacy_create_certificate, ['team_certificate_template']],
    [routesNames.pharmacy_edit_certificate, ['team_certificate_template']],
    [routesNames.pharmacy_task_management, ['team']],
    [routesNames.pharmacy_tasks, ['team']],
]

export const SUB_MODULE_KEYS_BY_PATH = ROUTE_SUB_MODULE_PAIRS.reduce((map, [route, keys]) => {
    if (!route) return map
    const path = String(route).split('?')[0]
    map[path] = [...new Set([...(map[path] || []), ...keys])]
    return map
}, {})

/**
 * The exact `route` string used by a nav item -> its sub-module key. Keyed by
 * the full string (query included) because Active/Inactive Patients share a
 * path and differ only by ?status.
 */
const NAV_SUB_MODULE_PAIRS = [
    [routesNames.ADD_PATIENTS, 'patients_add_patient'],
    [`${routesNames.ALL_PATIENTS}?status=1`, 'patients_active'],
    [`${routesNames.ALL_PATIENTS}?status=0`, 'patients_inactive'],
    [routesNames.pharmacy_patient_route, 'patients_route'],
    [routesNames.PHARMACY_INFORMATION_UPDATE_FORM, 'forms_pharmacy_information_update'],
    [routesNames.PHARMACY_PRESCRIPTION_TRASNSFER_FORM, 'forms_rx_transfer_request'],
    [routesNames.pharmacy_original_prescription_form, 'forms_original_rx_copy_request'],
    [routesNames.pharmacy_Emergency_Supply_OAT_medications_form, 'forms_dr_notification_emergency_supply'],
    [routesNames.pharmacy_medication_form, 'forms_anti_viral_request'],
    [routesNames.missing_Rx_Requirements, 'forms_missing_rx_requirements'],
    [routesNames.pharmacy_emergency_supply_continuity_form, 'forms_emergency_supply_continuity'],
    [routesNames.patient_agreement_for_take_home_dosing, 'forms_take_home_dosing_agreement'],
    [routesNames.pharmacy_Plan_G_Form, 'forms_plan_g'],
    [routesNames.pharmacy_Plan_Z_Form, 'forms_plan_z'],
    [routesNames.pharmacy_doctor_communication_form, 'forms_doctor_communication'],
    [routesNames.pharmacy_Pharmacist_Prescriber_Communication, 'forms_pharmacist_prescriber_communication'],
    [routesNames.pharmacy_doctor_note_form, 'forms_doctor_note'],
    [routesNames.pharmacy_tb_form, 'forms_tb_screening'],
    [routesNames.pharmacy_tb_skin_test_mantoux_form, 'forms_tb_skin_test_mantoux'],
    [routesNames.pharmacy_tb_skin_test_logs, 'forms_tb_skin_test_logs'],
    [routesNames.pharmacy_tb_screening_form_logs, 'forms_tb_screening_logs'],
    [routesNames.pharmacy_order_compounding_form, 'compound_order_compound'],
    [routesNames.pharmacy_compounds, 'compound_price_list'],
    [routesNames.pharmacy_order_compound_form, 'compound_order_form'],
    [routesNames.pharmacy_controlled_order_request, 'compound_controlled_order_request'],
    [routesNames.pharmacy_compound_agreement_form, 'compound_agreement_form'],
    [routesNames.pharmacy_compound_quotations, 'compound_quotation'],
    [routesNames.pharmacy_compound_due_billing, 'compound_due_billing'],
    [routesNames.pharmacy_archived_deliveries, 'dispense_archived_deliveries'],
    [routesNames.pharmacy_consent_form_logs, 'dispense_consent_submissions'],
    [routesNames.pharmacy_patient_counselling, 'dispense_counselling'],
    [routesNames.pharmacy_expiring_rx_v2, 'dispense_expiring_rx_v2'],
    [routesNames.pharmacy_expiring_rx, 'dispense_expiring_rx'],
    [routesNames.pharmacy_investigate_rx, 'dispense_investigation_rx'],
    [routesNames.pharmacy_rxnumber_date_sync, 'dispense_mark_delivered'],
    [routesNames.pharmacy_delivery_lables, 'dispense_labels'],
    [routesNames.pharmacy_patient_soap_notes, 'dispense_med_review'],
    [routesNames.pharmacy_med_review_logs, 'dispense_med_review_logs'],
    [routesNames.pharmacy_soap_notes_logs, 'dispense_soap_note_logs'],
    [routesNames.pharmacy_med_review_logs_v2, 'dispense_med_review_logs_v2'],
    [routesNames.pharmacy_tranfer_patient_concent, 'dispense_transfer_consents'],
    [routesNames.pharmacy_super_nova_verification, 'dispense_super_nova'],
    [routesNames.pharmacy_half_baked, 'dispense_half_baked'],
    [routesNames.pharmacy_drugs_billing, 'dispense_billing'],
    [routesNames.pharmacy_oat_prescriptions, 'dispense_prescriptions'],
    [routesNames.pharmacy_memo, 'dispense_memo'],
    [routesNames.pharmacy_ongoing_prescritiptions, 'dispense_ongoing_prescriptions'],
    [routesNames.pharmacy_returned_patches, 'dispense_returned_patches'],
    [routesNames.pharmacy_blister_patients, 'blisters_add_blister_pack'],
    [routesNames.pharmacy_blister_packs, 'blisters_blister_packs'],
    [routesNames.pharmacy_blister_daily_packs, 'blisters_daily_packs'],
    [routesNames.pharmacy_blister_pack_on_shelf, 'blisters_packs_on_shelf'],
    [routesNames.pharmacy_patient_vials_pack, 'vials_add_vials_pack'],
    [routesNames.pharmacy_vials_packs, 'vials_vials_packs'],
    [routesNames.pharmacy_dailyvials_packs, 'vials_daily_vials'],
    [routesNames.pharmacy_speciality_pack, 'speciality_medication_add_pack'],
    [routesNames.pharmacy_speciality_medication_packs, 'speciality_medication_packs'],
    [routesNames.pharmacy_dailymedication_packs, 'speciality_medication_daily'],
    [routesNames.pharmacy_generate_invoice, 'invoice_generate'],
    [routesNames.pharmacy_previous_invoices, 'invoice_previous'],
    [routesNames.ADD_ARV, 'arv_add'],
    [routesNames.ARV_LIST, 'arv_list'],
    [routesNames.pharmacy_daily_revenue, 'revenue_daily'],
    [routesNames.pharmacy_cash_change, 'revenue_cash_change'],
    [routesNames.pharmacy_deposit_info, 'revenue_deposit_info'],
    [routesNames.pharmacy_hand_cash, 'revenue_in_hand_cash'],
    [routesNames.create_new_fda_form, 'fda_form_create_new'],
    [routesNames.all_generated_fda_form_list, 'fda_form_all_generated'],
    [routesNames.pharmacy_expiring_frequent_dispense, 'fda_form_expiring'],
    [routesNames.pharmacy_expired_frequent_dispense, 'fda_form_expired'],
    [routesNames.pharmacy_frequent_dispensing_authorization_timeline, 'fda_form_doctor_timelines'],
    [routesNames.pharmacy_fda_audit, 'fda_form_audit'],
    [routesNames.pharmacy_inventory_list, 'inventory_list'],
    [routesNames.pharmacy_accountability_today_missed_rx, 'accountability_actions'],
    [routesNames.pharmacy_accountability_new_rx, 'accountability_new_rx'],
    [routesNames.pharmacy_accountability_prep_check, 'accountability_prep_check'],
    [routesNames.pharmacy_accountability_prep_for_new_rx, 'accountability_prep_for_new_rx'],
    [routesNames.pharmacy_accountability_after_process_prep_check, 'accountability_after_process_prep_check'],
    [routesNames.pharmacy_accountability_final_check, 'accountability_final_check'],
    [routesNames.pharmacy_investigate_rx, 'accountability_investigation_rx'],
    [routesNames.pharmacy_accountability_all_reports, 'accountability_reports'],
    [routesNames.pharmacy_accounts, 'accountability_financials'],
    [routesNames.pharmacy_pharmacare, 'accountability_pharmacare'],
    [routesNames.pharmacy_add_travel_pack, 'travel_patients'],
    [routesNames.pharmacy_travel_logs, 'travel_logs'],
    [routesNames.pharmacy_billing_due, 'travel_billing_due'],
    [routesNames.pharmacy_travel_returning_patients, 'travel_returning_patients'],
    [routesNames.pharmacy_vaccine_passport, 'vaccine_passport'],
    [routesNames.pharmacy_vaccine_logs, 'vaccine_certificate_logs'],
    [routesNames.pharmacy_juno_appointment, 'appointments_book'],
    [routesNames.pharmacy_appointment_logs, 'appointments_logs'],
    [routesNames.pharmacy_blister_packaging_processing, 'packaging_blister'],
    [routesNames.pharmacy_blister_packaging_processing, 'packaging_blister_create_order'],
    [routesNames.pharmacy_blister_packaging_prn_processing, 'packaging_blister_prn_processing'],
    [routesNames.pharmacy_blister_packaging_liquid_processing, 'packaging_blister_liquid_processing'],
    [routesNames.pharmacy_blister_packaging_orders, 'packaging_blister_incoming_orders'],
    [routesNames.pharmacy_blister_packaging_packmed_files, 'packaging_blister_my_files'],
    [routesNames.pharmacy_blister_packaging_invoices, 'packaging_blister_invoice'],
    [routesNames.pharmacy_blister_packaging_monthly_invoice, 'packaging_blister_monthly_invoice'],
    [routesNames.pharmacy_packaging_processing, 'packaging_pacmed'],
    [routesNames.pharmacy_packaging_processing, 'packaging_pacmed_create_order'],
    [routesNames.pharmacy_packaging_orders, 'packaging_pacmed_incoming_orders'],
    [routesNames.pharmacy_packaging_all_d_logs, 'packaging_pacmed_my_files'],
    [routesNames.pharmacy_packaging_din_management, 'packaging_pacmed_create_order'],
    [routesNames.pharmacy_packaging_invoices, 'packaging_pacmed_invoice'],
    [routesNames.pharmacy_packaging_prn_processing, 'packaging_pacmed_prn_processing'],
    [routesNames.pharmacy_packaging_liquid_processing, 'packaging_pacmed_liquid_processing'],
    [routesNames.pharmacy_packaging_monthly_invoice, 'packaging_pacmed_monthly_invoice'],
    [routesNames.pharmacy_packaging_analytics, 'packaging_analytics'],
    [routesNames.pharmacy_app_prescriptions, 'app_prescriptions'],
    [routesNames.pharmacy_app_notification_list, 'app_custom_notification'],
    [routesNames.pharmacy_app_pharmacy_services, 'app_pharmacy_services'],
    [routesNames.pharmacy_app_patient_chat, 'app_patient_chat'],
    [routesNames.pharmacy_user, 'team_your_team'],
    [routesNames.pharmacy_timeoff, 'team_time_off_request'],
    [routesNames.pharmacy_team_attendance, 'team_timesheet'],
    [routesNames.pharmacy_certificate_template, 'team_certificate_template'],
    [routesNames.pharmacy_assign_certificate, 'team_assign_template'],
    [routesNames.pharmacy_check_out_in, 'team_checkin_checkout_logs'],
    [routesNames.pharmacy_department, 'team_departments'],
    [routesNames.pharmacy_team_avalibility, 'team_schedule'],
]

export const SUB_MODULE_KEY_BY_NAV_ROUTE = NAV_SUB_MODULE_PAIRS.reduce((map, [route, key]) => {
    if (route && !map[route]) map[route] = key
    return map
}, {})

/**
 * Drop the entries of a nav list the user may not use. The ONE filter every nav
 * surface runs (top nav, sidebar, avatar menu), so a fourth surface can never
 * grow its own slightly-different rule. `has` is the caller's key test — pass
 * the `hasKey` from buildModuleGate and legacy mode keeps the list intact.
 */
export const filterNavItems = (items, has) =>
    (items || []).filter((item) => navItemAllowed(item?.route, has))

/**
 * Is this nav item allowed? `has` is the caller's key test.
 * An item with no sub-module key of its own is NOT restricted here — it is
 * still covered by its parent tab's gate.
 */
export const navItemAllowed = (route, has) => {
    if (!route) return true
    const key = SUB_MODULE_KEY_BY_NAV_ROUTE[route] || SUB_MODULE_KEY_BY_NAV_ROUTE[String(route).split('?')[0]]
    if (!key) {
        const pathKeys = SUB_MODULE_KEYS_BY_PATH[String(route).split('?')[0]]
        return !pathKeys || pathKeys.some(has)
    }
    return has(key)
}
